import { createApp } from 'vue';
import { AppFullscreen, Notify, Quasar } from 'quasar';
import { createPinia } from 'pinia';
import { registerSW } from 'virtual:pwa-register';

import 'styles/quasar.scss';
import 'styles/app.styles.scss';
import '@quasar/extras/roboto-font/roboto-font.css';
import '@quasar/extras/material-icons/material-icons.css';
import '@quasar/extras/mdi-v7/mdi-v7.css';

import { createGtag } from 'vue-gtag';

import App from './App.vue';
import router from './router/router';
import { store } from './store/store';
import { i18n } from '@/plugins/i18n';
import { setConfig, getConfig } from '@/config';
import axios from 'axios';
import { configureBrowserClients } from '@tfpl/api-client';
import { useUla } from '@/composables/useUla';
import { useKioskAuth } from '@/composables/useKioskAuth';
import { AUTH_RECOVERY_ATTEMPTED_KEY } from '@/constants';
import { setupStaleBundleRecovery } from '@/utils/staleBundleRecovery';

const pinia = createPinia();
const vueApp = createApp(App);

const initVueApp = () => {
  const config = getConfig();
  /******************************************************************************
   * QUASAR
   *****************************************************************************/
  vueApp.use(Quasar, {
    config: {
      notify: {
        position: 'bottom-right',
      },
      screen: {
        bodyClasses: true,
      },
    },
    plugins: { AppFullscreen, Notify },
  });

  /******************************************************************************
   * I18N
   *****************************************************************************/
  vueApp.use(i18n);
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  globalThis.i18n = i18n;

  /******************************************************************************
   * VUEX
   *****************************************************************************/
  vueApp.use(store);

  /******************************************************************************
   * PINIA
   *****************************************************************************/
  vueApp.use(pinia);

  /******************************************************************************
   * ROUTER
   *****************************************************************************/
  vueApp.use(router);

  /******************************************************************************
   * GTAG
   *****************************************************************************/
  vueApp.use(
    createGtag({
      tagId: 'G-XNW62MW9GF', // this id only work on https://hub2.techfides.app/
      pageTracker: { router },
    }),
  );

  /******************************************************************************
   * OPEN API CLIENT (@tfpl/api-client)
   *
   * TF_HUB_BACKEND_URL is the gateway URL (legacy env name). Auth interceptor
   * (id token) is registered per client instance.
   *
   * Self-healing auth: the interceptor first retries a 401 once with a
   * force-refreshed id token (handles the common stale/expired-token case
   * invisibly). Only if that retry is still 401 does `onUnauthorized` run. A
   * single 401 therefore still never disrupts the app.
   *
   * A 401 surviving a fresh token almost always means this tab is running a
   * stale cached bundle (an old service worker precache) that is incompatible
   * with the current backend — e.g. an old build still calling a removed
   * endpoint version. Logging out cannot fix stale code (it would just loop
   * login↔app), so recovery instead pulls fresh code exactly once: update the
   * service worker and hard reload. A sessionStorage guard prevents a reload
   * loop if fresh code still 401s; the flag is cleared on a successful auth boot
   * (see useAuthentication.refreshUser) so a later mid-session deploy can
   * recover again.
   *****************************************************************************/
  const { getIdToken } = useUla();
  const { hasKioskKey } = useKioskAuth();
  // Guards against a burst of parallel 401s triggering multiple reloads at once.
  let isRecoveringAuth = false;
  configureBrowserClients({
    baseUrl: config.TF_HUB_BACKEND_URL,
    // In kiosk mode there is no ULA iframe, so getIdToken() — which posts a
    // message to that iframe and resolves only on its reply — would hang
    // forever, stalling every request in the interceptor (no request is ever
    // sent). The kiosk authenticates via the SA-key interceptor instead, so
    // skip the id token entirely when a kiosk key is present.
    getIdToken: async (forceRefresh) => (hasKioskKey() ? '' : ((await getIdToken(forceRefresh)) ?? '')),
    onUnauthorized: async () => {
      // Kiosk sessions authenticate via SA key, not a cached ULA session.
      if (isRecoveringAuth || hasKioskKey()) return;
      // One recovery attempt per tab session — never reload-loop.
      if (globalThis.sessionStorage?.getItem(AUTH_RECOVERY_ATTEMPTED_KEY)) return;
      isRecoveringAuth = true;
      globalThis.sessionStorage?.setItem(AUTH_RECOVERY_ATTEMPTED_KEY, '1');
      try {
        const registrations = (await navigator.serviceWorker?.getRegistrations?.()) ?? [];
        await Promise.all(registrations.map((registration) => registration.update()));
      } catch (e) {
        console.warn('[auth-recovery] service worker update failed', e);
      }
      globalThis.location.reload();
    },
  });

  /******************************************************************************
   * APP READY
   *****************************************************************************/

  console.log('-> Vue app ready');
  vueApp.mount('#app');
};

setupStaleBundleRecovery(router);

const registratorUpdateInterval = 10 * 1000;
registerSW({
  onRegisteredSW: (swUrl, registration) => {
    if (registration) {
      const updateSW = async () => {
        if (!(!registration.installing && navigator)) return;

        if ('connection' in navigator && !navigator.onLine) return;

        const resp = await fetch(swUrl, {
          cache: 'no-store',
          headers: {
            cache: 'no-store',
            'cache-control': 'no-cache',
          },
        });

        if (resp?.status === 200) {
          await registration.update();
        }
      };

      updateSW().catch((e) => {
        console.warn('[auth-recovery] service worker update failed', e);
      });
      setInterval(updateSW, registratorUpdateInterval);
    }
  },
});

const nodeEnv = import.meta.env.MODE;
if (nodeEnv === 'development') {
  console.log('Starting vue app in development mode');
  initVueApp();
} else if (nodeEnv === 'production' || nodeEnv === 'test') {
  console.log(`Starting vue app in ${nodeEnv} mode`);
  try {
    // eslint-disable-next-line no-restricted-syntax -- bootstrap: static config endpoint served by the app's own express layer, not a TF API
    const response = await axios.get('/config');
    console.log('-> Config fetched from server');
    setConfig(response.data);
    initVueApp();
  } catch (e) {
    console.error('Can not fetch config from server');
    console.error(e);
  }
} else {
  console.error('Wrong vite mode');
}
